/**
 * Delete a tour.
 *
 * An active tour gets an extra warning naming how many people have already seen it, so the cost
 * of removing it is visible before confirming.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { Tour } from '../../Types/Tours.types';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const strongStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const alertStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  border: 1px solid var(--color-warning);
`;

const alertTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const alertTextStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface DeleteTourModalProps {
  open: boolean;
  tour?: Tour;
  /** People who saw the current revision, for the active-tour warning. */
  viewCount?: number;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: (tour: Tour) => void;
}

export const DeleteTourModal = ({
  open,
  tour,
  viewCount,
  isDeleting,
  onClose,
  onConfirm,
}: DeleteTourModalProps): JSX.Element => {
  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        O tour da página <span className={strongStyle}>{tour?.pageSlug}</span> em{' '}
        <span className={strongStyle}>{tour?.tenantSlug}</span> sai do ar e deixa de abrir para
        qualquer usuário. Esta ação não pode ser desfeita.
      </p>

      {tour?.isActive ? (
        <div className={alertStyle}>
          <span className={alertTitleStyle}>Este tour está ativo</span>
          <span className={alertTextStyle}>
            {viewCount === undefined
              ? 'Ele está no ar agora e some imediatamente para quem ainda não viu.'
              : `${viewCount.toLocaleString('pt-BR')} ${viewCount === 1 ? 'pessoa já viu' : 'pessoas já viram'} a revisão v${tour.version}. Ele some imediatamente para quem ainda não viu.`}
          </span>
        </div>
      ) : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='delete-tour-confirm'
          text={isDeleting ? 'Excluindo...' : 'Excluir tour'}
          disabled={isDeleting}
          onClick={() => {
            if (tour) onConfirm(tour);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Excluir tour'
      content={content}
      maxWidth='500px'
      hasButtonClose
      position='center'
    />
  );
};
